import type { ReactNode } from 'react';
import { motion } from 'motion/react';
import { X } from 'lucide-react';
import { cn } from '@/src/infrastructure/lib/utils';

interface Props {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
  children?: ReactNode;
  disableConfirm?: boolean;
}

export function ConfirmModal({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive,
  loading,
  onConfirm,
  onClose,
  children,
  disableConfirm,
}: Props) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="glass-panel rounded-2xl w-full max-w-md p-6 space-y-5"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
      >
        <div className="flex items-start justify-between gap-4">
          <h3 id="confirm-title" className="font-display text-lg font-bold leading-tight">
            {title}
          </h3>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-on-surface-variant hover:text-on-surface"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {description && (
          <p className="text-sm text-on-surface-variant leading-relaxed">{description}</p>
        )}
        {children}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-widest text-on-surface-variant hover:text-on-surface disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading || disableConfirm}
            className={cn(
              'px-5 py-2 rounded-lg text-xs font-bold uppercase tracking-widest text-white transition-colors disabled:opacity-50',
              destructive ? 'bg-red-600 hover:bg-red-500' : 'bg-electric-violet hover:bg-electric-violet/90',
            )}
          >
            {loading ? 'Working…' : confirmLabel}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
